export const calculateVelocityData = (magnitude_data, direction_data) => {
  if (!magnitude_data || !direction_data) {
    return [];
  }

  if (magnitude_data.ncols !== direction_data.ncols ||
      magnitude_data.nrows !== direction_data.nrows ||
      magnitude_data.lngTopLeft !== direction_data.lngTopLeft ||
      magnitude_data.latBotRight !== direction_data.latBotRight ||
      magnitude_data.time !== direction_data.time) {
    throw new Error('Magnitude and Direction meta-data does not match.');
  }

  const header_common = {};

  header_common.nx = magnitude_data.ncols;
  header_common.ny = magnitude_data.nrows;
  header_common.la1 = magnitude_data.latTopLeft;
  header_common.la2 = magnitude_data.latBotRight;
  header_common.lo1 = magnitude_data.lngTopLeft;
  header_common.lo2 = magnitude_data.lngBotRight;
  header_common.dx = Math.abs(header_common.lo2 - header_common.lo1) / header_common.nx;
  header_common.dy = Math.abs(header_common.la2 - header_common.la1) / header_common.ny;
  header_common.parameterUnit = 'kt';
  header_common.parameterCategory = 2;
  header_common.refTime = magnitude_data.time;

  const eastward_wind = {
    header: {
      ...header_common,
      parameterNumberName: 'eastward_wind',
      parameterNumber: 2
    },
    data: []
  };

  const northward_wind = {
    header: {
      ...header_common,
      parameterNumberName: 'northward_wind',
      parameterNumber: 3
    },
    data: []
  };

  const len = magnitude_data.values.length;
  const to_rads = Math.PI / 180;

  for (let i = 0; i < len; i++) {
    const rads = direction_data.values[i] * to_rads;
    northward_wind.data.push(magnitude_data.values[i] * -Math.cos(rads));
    eastward_wind.data.push(magnitude_data.values[i] * -Math.sin(rads));
  }

  return [eastward_wind, northward_wind];
};

export const getRefTime = (data) => {
  if (data && data[0] && data[0].header) {
    return data[0].header.refTime;
  }
  return null;
};

export const parseWcs = (text, time) => {
  const data = {};
  const lines = text.split(/\r?\n/);

  data.time = time;

  // Parse Grid bounds: GeneralBounds[(xmin, ymin), (xmax, ymax)]
  const boundsMatch = lines[0].match(/GeneralBounds\[\(([^,]+),\s*([^)]+)\),\s*\(([^,]+),\s*([^)]+)\)]/);
  if (!boundsMatch) {
    throw new Error('Expected Grid bounds line: ' + lines[0]);
  }

  const minLng = Number(boundsMatch[1]);
  const minLat = Number(boundsMatch[2]);
  const maxLng = Number(boundsMatch[3]);
  const maxLat = Number(boundsMatch[4]);

  data.lngTopLeft = minLng;
  data.lngBotRight = maxLng;
  data.latTopLeft = maxLat;
  data.latBotRight = minLat;

  // Find Grid range line and the start of the data rows
  let gridRangeLine = null;
  let dataStartLine = -1;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].startsWith('Grid range:')) {
      gridRangeLine = lines[i];
    }
    if (lines[i].startsWith('Band 0:')) {
      dataStartLine = i + 1;
      break;
    }
  }

  // Parse the number of rows and columns
  if (!gridRangeLine) {
    throw new Error('Expected Grid range line');
  }
  const rangeMatch = gridRangeLine.match(/GridEnvelope2D\[0\.\.(\d+),\s*0\.\.(\d+)]/);
  if (!rangeMatch) {
    throw new Error('Expected Grid range format: ' + gridRangeLine);
  }
  data.ncols = Number(rangeMatch[1]) + 1;
  data.nrows = Number(rangeMatch[2]) + 1;

  // Parse the data values
  if (dataStartLine < 0) {
    throw new Error('Expected Band 0: line');
  }

  data.values = [];
  for (let i = dataStartLine; i < lines.length; i++) {
    if (!lines[i].trim()) {
      continue;
    }
    const values = lines[i].split(/ /);
    values.forEach((value) => {
      if (value === 'NaN' || value === '') {
        data.values.push(0);
      } else {
        data.values.push(Number(value));
      }
    });
  }

  return data;
};

export const convertKtsToKph = function (value) {
  return (value ? value * 1.852 : 0);
};

export const toCardinal = (degrees) => {
  let val = Math.floor((degrees / 22.5) + 0.5);
  let arr = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  return arr[(val % 16)];
};

export const toCardinalAndArrow = function (angle) {
  let val = Math.floor((angle / 22.5) + 0.5);
  let arr = ['↓ N',
             '↙ NNE',
             '↙ NE',
             '↙ ENE',
             '← E',
             '↖ ESE',
             '↖ SE',
             '↖ SSE',
             '↑ S',
             '↗ SSW',
             '↗ SW',
             '↗ WSW',
             '→ W',
             '↘ WNW',
             '↘ NW',
             '↘ NNW'];
  return arr[(val % 16)];
};

// Returns the GeoJSON defining the wind layer as a set of points.
export const calculateBarbData = (magnitude_data, direction_data, map_bounds, map_zoom, time) => {
  if (!magnitude_data || !direction_data || !magnitude_data.ncols || !direction_data.ncols) {
    return null;
  }

  if (magnitude_data.ncols !== direction_data.ncols ||
      magnitude_data.nrows !== direction_data.nrows ||
      magnitude_data.lngTopLeft !== direction_data.lngTopLeft ||
      magnitude_data.latBotRight !== direction_data.latBotRight) {
    throw new Error('Magnitude and Direction meta-data does not match.');
  }

  const geoJson = {};
  geoJson.type = 'FeatureCollection';
  geoJson.features = [];

  // To force the layer to render, the key should change if the data changes.
  geoJson.key = map_bounds ? map_bounds._northEast.lat + map_bounds._northEast.lng + time : time;

  // Mapping zoom levels to layer resolution
  //const resolutions = [48, 48, 48, 48, 48, 48, 48, 24, 12, 6, 3, 2, 1, 1, 1, 1, 1, 1, 1, 1, 1];

  const ncols = magnitude_data.ncols;
  const nrows = magnitude_data.nrows;
  const data_la1 = magnitude_data.latTopLeft;
  const data_la2 = magnitude_data.latBotRight;
  const data_lo1 = magnitude_data.lngTopLeft;
  const data_lo2 = magnitude_data.lngBotRight;
  const dx = Math.abs(data_lo2 - data_lo1) / ncols;
  const dy = Math.abs(data_la2 - data_la1) / nrows;
  const colCount = 15;
  const rowCount = 13;
  const colOffset = Math.floor(direction_data.ncols / colCount);
  const rowOffset = Math.floor(direction_data.nrows / rowCount);

  for (let row = 0; row < nrows; row++) {
    for (let col = 0; col < ncols; col++) {
      const i = row * ncols + col;
      if (magnitude_data.values[i] > 0 && row % rowOffset === 0 && col % colOffset === 0) {
        const lat = data_la1 - row * dy;
        const lng = data_lo1 + col * dx;
        if (geoJson.features.length < (13 * 15)) {
          geoJson.features.push(
            _createBarbFeature(lat, lng, direction_data.values[i], magnitude_data.values[i]));
        }
      }
    }
  }
  return geoJson;
};

const _createBarbFeature = (lat, lng, deg, speedInKts) => {
  const speedInKph = convertKtsToKph(speedInKts);
  const feature = {};
  feature.type = 'Feature';
  feature.properties = { deg, speed: speedInKph };
  feature.geometry = {
    type: 'Point',
    coordinates: [lng, lat]
  };
  return feature;
};

export const boundsEqual = (bounds1, bounds2) => {
  return bounds1._northEast.lat === bounds2._northEast.lat &&
         bounds1._northEast.lng === bounds2._northEast.lng &&
         bounds1._southWest.lat === bounds2._southWest.lat &&
         bounds1._southWest.lng === bounds2._southWest.lng;
};



// WEBPACK FOOTER //
// ./src/utils/wind_utils.js